import React, { useCallback, useMemo, useState } from "react"
import { Immutable } from "@hookstate/core"
import { useNotificationsDataState } from "../../globalStates/NotificationDataState"
import { useLanguageState } from "../../globalStates/LanguageState"
import { RefCategory, useReferenceDataState } from "../../globalStates/ReferenceDataState"
import { NotificationTemplateInterest } from "../../globalStates/ExhibitorDataTypes"
import { isNullOrEmptyRO } from "../../utils/Arrays"
import TreeCheck, { DownArrow, NodeText, RightArrow, TreeData, TreeItemData, TreeItemWrapper } from "../../components/TreeCheck"
import SearchField from "../../components/Search"

function selectAllParents(node: TreeItemData) {
    if (node.parent) {
        // console.log('parent ' + node.parent.id + '/' + node.parent.text + ' checked')
        node.parent.checked = true
        selectAllParents(node.parent)
    }
}

function isEligible(refCategory: Immutable<RefCategory>, filter: string) {
    const eligible = refCategory.name && refCategory.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0

    return eligible
}

function makeNode(
    refCategory: Immutable<RefCategory>,
    subNodes: TreeItemData[],
    selectedCategories: Map<string, boolean>,
    rootCategoryId: string | null
): TreeItemData {
    return {
        id: refCategory.id || "",
        rootId: rootCategoryId || "",
        text: refCategory.name || "",
        code: refCategory.code || "",
        selected: !!refCategory.id && !!selectedCategories.get(refCategory.id),
        checked: false,
        relevant: false,
        parent: null,
        nodes: subNodes
    }
}

function mapAllCategories2Nodes(
    parent: TreeItemData,
    refCategories: Immutable<RefCategory[]>,
    selectedCategories: Map<string, boolean>,
    rootCategoryId: string | null
): TreeItemData[] {
    // console.log('mapAllCategories2Nodes ' + parent.id)
    return refCategories.map((refCategory) => {
        const node = makeNode(refCategory, [], selectedCategories, rootCategoryId)
        node.parent = parent
        if (node.selected) selectAllParents(node)
        if (refCategory.subCategories) {
            node.nodes = mapAllCategories2Nodes(node, refCategory.subCategories, selectedCategories, rootCategoryId)
        }
        return node
    })
}

function mapCategory2Node(
    refCategory: Immutable<RefCategory>,
    filter: string,
    selectedCategories: Map<string, boolean>,
    rootCategoryId: string | null
): TreeItemData | null {
    // console.log('mapping ' + refCategory.name)
    let currentNode: TreeItemData | null = null

    if (isEligible(refCategory, filter)) {
        currentNode = makeNode(refCategory, [], selectedCategories, rootCategoryId)
        currentNode.relevant = !!filter
        if (refCategory.subCategories) {
            currentNode.nodes = mapAllCategories2Nodes(currentNode, refCategory.subCategories, selectedCategories, rootCategoryId)
        }

        return currentNode
    }

    if (!isNullOrEmptyRO(refCategory.subCategories)) {
        refCategory.subCategories?.forEach((refSubCategory) => {
            const subNode = mapCategory2Node(refSubCategory, filter, selectedCategories, rootCategoryId)
            if (subNode) {
                if (!currentNode) {
                    // create current node on demand
                    currentNode = makeNode(refCategory, [], selectedCategories, rootCategoryId)
                }
                subNode.parent = currentNode
                if (subNode.selected) selectAllParents(subNode)
                currentNode.nodes.push(subNode)
            }
        })
    }

    return currentNode
}

function mapCategories2Nodes(
    refCategories: Immutable<RefCategory[]> | null,
    filter: string,
    selectedCategories: Map<string, boolean>
): TreeData {
    const nodes: TreeItemData[] = []

    refCategories?.forEach((refCategory) => {
        const node = mapCategory2Node(refCategory, filter, selectedCategories, refCategory.id)
        if (node) {
            nodes.push(node)
        }
    })
    return { nodes }
}

function selectedInterestsToMap(selInterests: Immutable<NotificationTemplateInterest[]> | null): Map<string, boolean> {
    let selection = new Map()
    selInterests?.forEach((cat) => {
        selection.set(cat.id, true)
    })
    return selection
}

interface InterestsProps {
    interests: NotificationTemplateInterest[]
    refCategories: Immutable<RefCategory[]>
    addInterest: (id: string, credits?: string) => void
    removeInterest: (id: string, credits?: string) => void
}

const NotificationTemplateInterests: React.FC<InterestsProps> = ({ interests, refCategories, addInterest, removeInterest }) => {
    const branding = useLanguageState().getStrings()
    const notifState = useNotificationsDataState()
    const [filter, setFilter] = useState("")
    const refDataState = useReferenceDataState()
    const selected = useMemo(() => selectedInterestsToMap(interests), [interests])
    const root = useMemo(() => mapCategories2Nodes(refCategories, filter, selected), [refCategories, filter, selected])
    const [expanded, setExpanded] = useState(false)

    const isSelected = (interestId: string): boolean => {
        return interests.findIndex((interest: NotificationTemplateInterest) => interest.id === interestId) > -1
    }

    const onSelectionChangedHandler = useCallback(
        (id: string, rootId: string, credits?: string) => {
            const toRemoveCheck: boolean = isSelected(id)

            toRemoveCheck ? removeInterest(id, credits) : addInterest(id, credits)
        },
        // eslint-disable-next-line
        [notifState, refDataState, root, selected]
    )

    const onChangeSearchHandler = useCallback((searchFilter: string) => {
        setFilter(searchFilter)
    }, [])

    return (
        <>
            <SearchField onChange={onChangeSearchHandler} />
            <TreeItemWrapper className="interestsWrapper" unitsLeft={0}>
                <NodeText mainColor={branding.configuration.maincolor} onClick={() => setExpanded(!expanded)}>
                    {branding.configuration.notificationTemplatesPage.sctNotificationTemplates.lblInterests}
                </NodeText>
                {expanded && <DownArrow />}
                {!expanded && <RightArrow />}
            </TreeItemWrapper>
            {expanded && (
                <TreeCheck
                    showCode={branding.configuration.showCategoriesCode}
                    rootNode={root}
                    selection={selected}
                    onSelectionChanged={onSelectionChangedHandler}
                    includeCredits
                />
            )}
        </>
    )
}

export default NotificationTemplateInterests
